// SixDetailServiceImpl.ts
import { SixDetailService } from "@/services/abstract/SixDetailService";
import type { FetcherService } from "@/services/abstract/FetcherService";
import { sixEndpoints } from "@/api/endpoints";
import type { ParticipantsResponseDTO, SixActivityDTO, SixFileDTO, SixFolderDTO, SixDetailDTO, DeleteFileResponse, SixFilesApiResponse } from "@/types/six/SixDTOs";

/**
 * Implementation of Six detail service for managing individual Six workspaces.
 *
 * Provides functionality for:
 * - Retrieving Six metadata and participants
 * - Managing files and folders within a Six
 * - Fetching and exporting activity logs
 *
 * @see SixDetailService
 */
export class SixDetailServiceImpl extends SixDetailService {
  /**
   * Creates an instance of SixDetailServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  /**
   * Retrieves metadata and participant information for a specific Six
   * @endpoint GET /six/{id}/meta
   * @param id - The unique identifier of the Six
   * @returns SixSummary object with extended participant details
   * @throws NotFoundError if Six with given ID does not exist
   * @throws UnauthorizedError if user lacks permission to view this Six
   * @tags six, metadata, participants
   */
  async getMeta(id: string): Promise<SixDetailDTO> {
  return await this.fetcher.get<SixDetailDTO>(sixEndpoints.getSix(id));
  }

  /**
   * Retrieves all files and folders within a Six workspace
   * @endpoint GET /six/{id}/files
   * @param id - The unique identifier of the Six
   * @returns Object containing files array and folders array
   * @throws NotFoundError if Six with given ID does not exist
   * @throws UnauthorizedError if user lacks permission to view this Six's files
   * @tags six, files, folders
   */
  async fetchFiles(id: string): Promise<SixFilesApiResponse> {
    return await this.fetcher.get<SixFilesApiResponse>(sixEndpoints.getSixFiles(id));
  }

  /**
   * Uploads a file to a Six workspace
   * @endpoint POST /six/{id}/files/upload
   * @param id - The unique identifier of the Six
   * @param file - The File object to upload
   * @param path - Optional folder path where the file should be uploaded (e.g., '/Documents/')
   * @request FormData with 'file' field containing the uploaded file
   * @returns SixFile object representing the newly uploaded file
   * @throws NotFoundError if Six with given ID does not exist
   * @throws UnauthorizedError if user lacks permission to upload files to this Six
   * @throws ValidationError if file type is not allowed or file size exceeds limit
   * @tags six, files, upload
   */
  async uploadFile(id: string, file: File, path?: string): Promise<SixFileDTO> {
    const formData = new FormData();
    formData.append("file", file);
    if (path !== undefined && path !== '/') {
      formData.append("path", path);
    }
    return await this.fetcher.post<SixFileDTO>(sixEndpoints.uploadSixFiles(id), formData);
  }

  /**
   * Creates a new folder within a Six workspace
   * @endpoint POST /six/{id}/folders
   * @param id - The unique identifier of the Six
   * @param folderPath - The full path for the new folder (e.g., '/Documents/Legal/')
   * @request { path: string }
   * @returns SixFolderDTO representing the newly created folder
   * @throws NotFoundError if Six with given ID does not exist
   * @throws UnauthorizedError if user lacks permission to create folders in this Six
   * @throws ValidationError if folder path is invalid or already exists
   * @throws ConflictError if folder path conflicts with existing file or folder
   * @tags six, folders, creation
   */
  async createFolder(id: string, folderName:string, folderPath: string): Promise<SixFolderDTO> {
    return await this.fetcher.post<SixFolderDTO>(sixEndpoints.createSixFolder(id), { folderPath, folderName });
  }

  /**
   * Permanently deletes a folder from a Six workspace
   * Backend handles cascade deletion of all contents within the folder.
   * @endpoint DELETE /sixes/{sixId}/folder
   * @param sixId - The unique identifier of the Six
   * @param folderPath - The full path of the folder to delete
   * @returns DeleteFileResponse containing folderId
   * @throws NotFoundError if Six or folder does not exist
   * @throws UnauthorizedError if user lacks permission to delete folders
   * @tags six, folders, delete
   */
  async deleteFolder(sixId: string, folderPath: string): Promise<DeleteFileResponse> {
    const url = sixEndpoints.deleteFolder(sixId);
    return await this.fetcher.del<DeleteFileResponse>(url, {folderPath});
  }

  /**
   * Retrieves the activity log for a Six workspace
   *
   * Returns a chronological list of activities including file uploads,
   * user actions, and other events within the Six.
   *
   * @endpoint GET /six/{id}/activity
   * @param id - The unique identifier of the Six
   * @returns Array of SixActivity objects representing activity history
   * @throws NotFoundError if Six with given ID does not exist
   * @throws UnauthorizedError if user lacks permission to view this Six's activity
   * @tags six, activity, history
   */
  async fetchActivity(id: string): Promise<SixActivityDTO[]> {
    return await this.fetcher.get<SixActivityDTO[]>(sixEndpoints.getSixActivity(id));
  }

  /**
   * Exports the activity log for a Six workspace as a CSV file
   * @endpoint GET /six/{id}/activity/export
   * @param id - The unique identifier of the Six
   * @returns Blob containing the CSV file data
   * @throws NotFoundError if Six with given ID does not exist
   * @throws UnauthorizedError if user lacks permission to export this Six's activity
   * @tags six, activity, export, csv
   */
  async exportActivityCsv(id: string): Promise<Blob> {
    return await this.fetcher.get<Blob>(sixEndpoints.exportSix(id), { responseType: "blob" });
  }

  async fetchParticipants(id: string): Promise<ParticipantsResponseDTO> {
    return await this.fetcher.get<ParticipantsResponseDTO>(sixEndpoints.getSixParticipants(id))
  }

  /**
   * Marks a file as downloaded by the current user
   * @endpoint POST /sixes/{sixId}/file/{fileId}/downloaded
   * @param sixId - The unique identifier of the Six
   * @param fileId - The unique identifier of the file being marked as downloaded
   * @throws NotFoundError if Six or file does not exist
   * @throws UnauthorizedError if user lacks permission
   * @tags six, files, download
   */
  async markFileDownloaded(sixId: string, fileId: string): Promise<void> {
    await this.fetcher.post(sixEndpoints.markFileDownloaded(sixId, fileId), {});
  }

  /**
   * Permanently deletes a file from a Six workspace
   * @endpoint DELETE /sixes/{sixId}/file/{fileId}
   * @param sixId - The unique identifier of the Six
   * @param fileId - The unique identifier of the file to delete
   * @returns DeleteFileResponse containing fileId (and optional commandId for polling)
   * @throws NotFoundError if Six or file does not exist
   * @throws UnauthorizedError if user lacks permission to delete files
   * @tags six, files, delete
   */
  async deleteFile(sixId: string, fileId: string): Promise<DeleteFileResponse> {
    const url = sixEndpoints.deleteFile(sixId, fileId);
    return await this.fetcher.del<DeleteFileResponse>(url);
  }
}
